import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { NumericInput, Slider, Spinner, Intent, Button as BPButton } from "@blueprintjs/core";
import { Button } from "components/Button";
import { Colors } from "design/colors";
import { useDevices } from "hooks/useDevices";
import { usePlayback } from "hooks/usePlayback";
import { packetThreadPoolApi } from "utils/packet-workers";
import { throttle, filenameWithTimestamp, downloadText } from "utils/common";
import { hidSend } from "connections/hid";
import { ControlMode } from "utils/common-types";
import {
  InputMode,
  serializeSetUsbThrottleControlRequest,
  serializeStartSysidRequest,
  serializeStopSysidRequest,
} from "utils/common-types";
import {
  INavigationProps,
  FORM_WIDTH,
  configContainerStyle,
  screenTitleStyle,
} from "./CStoreEditor";

const REFRESH_MS = 50;
const PULSE_INTERVAL_MS = 50;
const PLOT_WIDTH = 500;
const PLOT_HEIGHT = 250;
const PLOT_PADDING = { top: 20, right: 20, bottom: 40, left: 60 };

interface SysIDTelemetry {
  excitationFrequency: number | undefined;
  closedLoopMagnitude: number | undefined;
  closedLoopPhase: number | undefined;
  openLoopMagnitude: number | undefined;
  openLoopPhase: number | undefined;
  motorSpeed: number | undefined;
  motorPosition: number | undefined;
}

interface FrequencyDataPoint {
  frequency: number;
  closedLoopMagnitude: number;
  closedLoopPhase: number;
  openLoopMagnitude: number;
  openLoopPhase: number;
  timestamp: number;
}

const toNumber = (val: number | boolean | null): number | undefined => {
  if (val === null || typeof val === "boolean") return undefined;
  return val;
};

/**
 * Hook to get SysID telemetry values from the realtime data stream.
 *
 * Field mapping (firmware repurposes fields during SysID):
 *   - sysidFrequency  -> Excitation frequency (Hz)
 *   - sysidMagnitude  -> Closed-loop magnitude
 *   - motorCurrent    -> Closed-loop phase (deg)
 *   - batteryVoltage  -> Open-loop magnitude
 *   - batteryCurrent  -> Open-loop phase (deg)
 */
const useSysIDTelemetry = (): SysIDTelemetry => {
  const { timeWindow } = usePlayback();
  const [telemetry, setTelemetry] = useState<SysIDTelemetry>({
    excitationFrequency: undefined,
    closedLoopMagnitude: undefined,
    closedLoopPhase: undefined,
    openLoopMagnitude: undefined,
    openLoopPhase: undefined,
    motorSpeed: undefined,
    motorPosition: undefined,
  });

  const fetchTelemetry = useMemo(
    () =>
      throttle(async (latestTimeMs: number) => {
        try {
          const [
            frequency,
            clMagnitude,
            clPhase,
            olMagnitude,
            olPhase,
            speed,
            position
          ] = await Promise.all([
            packetThreadPoolApi.getLatestTelemValueNotAfter("sysidFrequency", latestTimeMs),
            packetThreadPoolApi.getLatestTelemValueNotAfter("sysidMagnitude", latestTimeMs),
            packetThreadPoolApi.getLatestTelemValueNotAfter("motorCurrent", latestTimeMs),
            packetThreadPoolApi.getLatestTelemValueNotAfter("batteryVoltage", latestTimeMs),
            packetThreadPoolApi.getLatestTelemValueNotAfter("batteryCurrent", latestTimeMs),
            packetThreadPoolApi.getLatestTelemValueNotAfter("motorSpeed", latestTimeMs),
            packetThreadPoolApi.getLatestTelemValueNotAfter("motorPosition", latestTimeMs),
          ]);

          setTelemetry({
            excitationFrequency: toNumber(frequency),
            closedLoopMagnitude: toNumber(clMagnitude),
            closedLoopPhase: toNumber(clPhase),
            openLoopMagnitude: toNumber(olMagnitude),
            openLoopPhase: toNumber(olPhase),
            motorSpeed: toNumber(speed),
            motorPosition: toNumber(position),
          });
        } catch (e) {
          console.warn("Failed to fetch SysID telemetry:", e);
        }
      }, REFRESH_MS),
    []
  );

  useEffect(() => {
    const latestTimeMs = timeWindow?.[1];
    if (latestTimeMs === undefined) {
      return;
    }
    fetchTelemetry(latestTimeMs);
  }, [timeWindow, fetchTelemetry]);

  return telemetry;
};

/**
 * Frequency Response Plot Component
 */
type PlotYKey = "closedLoopMagnitude" | "closedLoopPhase" | "openLoopMagnitude" | "openLoopPhase";

interface FrequencyResponsePlotProps {
  dataPoints: FrequencyDataPoint[];
  width: number;
  height: number;
  title: string;
  yLabel: string;
  yKey: PlotYKey;
  color: string;
}

const FrequencyResponsePlot = ({
  dataPoints,
  width,
  height,
  title,
  yLabel,
  yKey,
  color,
}: FrequencyResponsePlotProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isPhase = yKey.includes("Phase");

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Background
    ctx.fillStyle = Colors.DARK_GREEN;
    ctx.fillRect(0, 0, width, height);

    const plotWidth = width - PLOT_PADDING.left - PLOT_PADDING.right;
    const plotHeight = height - PLOT_PADDING.top - PLOT_PADDING.bottom;

    // Grid
    ctx.strokeStyle = "#444";
    ctx.lineWidth = 1;

    const frequencies = dataPoints.map((p) => p.frequency);
    const yValues = dataPoints.map((p) => p[yKey]);

    const maxFreq = Math.max(110, ...frequencies);
    const minY = isPhase ? Math.min(-180, ...yValues) : 0;
    const maxY = isPhase ? Math.max(0, ...yValues) : Math.max(2, ...yValues);
    const yRange = maxY - minY;

    // Vertical grid lines
    const freqSteps = [1, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
    ctx.beginPath();
    freqSteps.forEach((freq) => {
      const x = PLOT_PADDING.left + (freq / maxFreq) * plotWidth;
      ctx.moveTo(x, PLOT_PADDING.top);
      ctx.lineTo(x, height - PLOT_PADDING.bottom);
    });
    ctx.stroke();

    // Horizontal grid lines
    const yStepCount = 5;
    ctx.beginPath();
    for (let i = 0; i <= yStepCount; i++) {
      const y = PLOT_PADDING.top + (i / yStepCount) * plotHeight;
      ctx.moveTo(PLOT_PADDING.left, y);
      ctx.lineTo(width - PLOT_PADDING.right, y);
    }
    ctx.stroke();

    // Axes
    ctx.strokeStyle = Colors.WHITE;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(PLOT_PADDING.left, PLOT_PADDING.top);
    ctx.lineTo(PLOT_PADDING.left, height - PLOT_PADDING.bottom);
    ctx.lineTo(width - PLOT_PADDING.right, height - PLOT_PADDING.bottom);
    ctx.stroke();

    // Labels
    ctx.fillStyle = Colors.WHITE;
    ctx.font = "11px sans-serif";
    ctx.textAlign = "center";

    // X-axis labels
    freqSteps.forEach((freq) => {
      const x = PLOT_PADDING.left + (freq / maxFreq) * plotWidth;
      ctx.fillText(freq.toString(), x, height - PLOT_PADDING.bottom + 12);
    });
    ctx.fillText("Frequency (Hz)", width / 2, height - 3);

    // Y-axis labels
    ctx.textAlign = "right";
    for (let i = 0; i <= yStepCount; i++) {
      const y = PLOT_PADDING.top + (i / yStepCount) * plotHeight;
      const value = maxY - (i / yStepCount) * yRange;
      ctx.fillText(value.toFixed(isPhase ? 0 : 2), PLOT_PADDING.left - 5, y + 4);
    }

    // Y-axis title
    ctx.save();
    ctx.translate(12, height / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.textAlign = "center";
    ctx.fillText(yLabel, 0, 0);
    ctx.restore();

    // Title
    ctx.textAlign = "center";
    ctx.font = "bold 13px sans-serif";
    ctx.fillText(title, width / 2, 14);

    // Data points and lines
    if (dataPoints.length > 0) {
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath();

      const sortedPoints = [...dataPoints].sort((a, b) => a.frequency - b.frequency);

      sortedPoints.forEach((point, index) => {
        const x = PLOT_PADDING.left + (point.frequency / maxFreq) * plotWidth;
        const y = PLOT_PADDING.top + ((maxY - point[yKey]) / yRange) * plotHeight;

        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      ctx.stroke();

      // Draw points
      ctx.fillStyle = color;
      sortedPoints.forEach((point) => {
        const x = PLOT_PADDING.left + (point.frequency / maxFreq) * plotWidth;
        const y = PLOT_PADDING.top + ((maxY - point[yKey]) / yRange) * plotHeight;

        ctx.beginPath();
        ctx.arc(x, y, 3, 0, 2 * Math.PI);
        ctx.fill();
      });
    }
  }, [dataPoints, width, height, title, yLabel, yKey, color, isPhase]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{ borderRadius: 4 }}
    />
  );
};

export const SysIDScreen = (props: INavigationProps) => {
  const { connectedDevice } = useDevices();
  const { next, prev } = props;
  const sysIdTelemetry = useSysIDTelemetry();

  // Safe access to device state
  const isConnected = connectedDevice !== undefined && connectedDevice.initialized;
  const latestConfig = isConnected ? connectedDevice.inputConfig : undefined;
  const controlMode = isConnected ? connectedDevice.controlMode : undefined;
  const isUsbMode = latestConfig?.inputMode === InputMode.USB;
  const isPositionMode = controlMode === ControlMode.POSITION;
  const deviceHandle = isConnected ? connectedDevice.handle : undefined;

  // Get position limits from device config directly (useThrottleConfig crashes this screen)
  // For POSITION mode: slider range = [IP_Lower, IP_Upper], value sent directly (1:1)
  const inputLower = latestConfig?.inputLimitLower ?? 0;
  const inputCenter = latestConfig?.inputLimitCenter ?? 2000;
  const inputUpper = latestConfig?.inputLimitUpper ?? 4000;
  const stepSize = 1;

  // Throttle state - default to center position
  const [throttleValue, _setThrottleValue] = useState<number>(inputCenter);
  const throttleValueRef = useRef<number>(inputCenter);
  const [manualValueStr, setManualValueStr] = useState<string>("");
  const pauseThrottleRef = useRef<boolean>(document.hidden);

  // Enable/disable state (like Position widget Start/Stop)
  const [isEnabled, setIsEnabled] = useState<boolean>(false);
  const isEnabledRef = useRef<boolean>(false);

  // SysID running state - tracks user's requested state (not just telemetry)
  const [sysIdRequested, setSysIdRequested] = useState<boolean>(false);

  // Collected frequency response data
  const [dataPoints, setDataPoints] = useState<FrequencyDataPoint[]>([]);
  const lastFrequencyRef = useRef<number | undefined>(undefined);

  const setThrottleValue = useMemo(() => throttle(_setThrottleValue, REFRESH_MS), [_setThrottleValue]);

  // Update throttle to center when config changes (device connects)
  useEffect(() => {
    if (latestConfig) {
      const center = latestConfig.inputLimitCenter ?? 2000;
      _setThrottleValue(center);
      throttleValueRef.current = center;
    }
  }, [latestConfig]);

  // Visibility change handler
  useEffect(() => {
    const onVisibilityChange = () => {
      pauseThrottleRef.current = document.hidden;
    };
    window.addEventListener("visibilitychange", onVisibilityChange);
    return () => window.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  // Update ref when throttle value changes
  useEffect(() => {
    throttleValueRef.current = throttleValue;
  }, [throttleValue]);

  // Send position command pulses only when enabled (like Position widget)
  // For POSITION mode: send throttle value directly (1:1 correspondence per physicalToDevice)
  const sendPulse = useCallback(() => {
    if (!isEnabledRef.current || !deviceHandle || !latestConfig || pauseThrottleRef.current) {
      return;
    }

    // Position mode: value sent directly (same as physicalToDevice for ControlMode.POSITION)
    hidSend(
      deviceHandle,
      serializeSetUsbThrottleControlRequest({
        value: throttleValueRef.current,
      })
    ).catch((e) => console.warn("Failed to send position command:", e));
  }, [deviceHandle, latestConfig]);

  // Command sending interval - only runs when enabled
  useEffect(() => {
    if (!isEnabled) {
      return;
    }
    sendPulse();
    const interval = setInterval(sendPulse, PULSE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [sendPulse, isEnabled]);

  // Throttle onChange handler
  const onThrottleChange = useCallback((value: number) => {
    setThrottleValue(value);
  }, [setThrottleValue]);

  // Manual value submit handler
  const updateWithManualValue = useCallback(() => {
    const manualValue = parseFloat(manualValueStr);
    if (!isFinite(manualValue)) {
      return;
    }
    // Clamp to [inputLower, inputUpper]
    const value = Math.max(Math.min(manualValue, inputUpper), inputLower);
    setThrottleValue(value);
    setManualValueStr("");
  }, [manualValueStr, setThrottleValue, inputLower, inputUpper]);

  // Collect data points as frequency changes
  useEffect(() => {
    const freq = sysIdTelemetry.excitationFrequency;
    const clMag = sysIdTelemetry.closedLoopMagnitude;
    const clPhase = sysIdTelemetry.closedLoopPhase;
    const olMag = sysIdTelemetry.openLoopMagnitude;
    const olPhase = sysIdTelemetry.openLoopPhase;

    if (freq === undefined || clMag === undefined || clPhase === undefined) return;
    if (freq <= 0) return;

    const lastFreq = lastFrequencyRef.current;
    if (lastFreq !== undefined && Math.abs(freq - lastFreq) < 0.1) return;

    setDataPoints((prev) => {
      const existingIndex = prev.findIndex((p) => Math.abs(p.frequency - freq) < 0.5);

      const newPoint: FrequencyDataPoint = {
        frequency: freq,
        closedLoopMagnitude: clMag,
        closedLoopPhase: clPhase,
        openLoopMagnitude: olMag ?? 0,
        openLoopPhase: olPhase ?? 0,
        timestamp: Date.now(),
      };

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = newPoint;
        return updated;
      } else {
        return [...prev, newPoint];
      }
    });

    lastFrequencyRef.current = freq;
  }, [
    sysIdTelemetry.excitationFrequency,
    sysIdTelemetry.closedLoopMagnitude,
    sysIdTelemetry.closedLoopPhase,
    sysIdTelemetry.openLoopMagnitude,
    sysIdTelemetry.openLoopPhase,
  ]);

  const clearData = useCallback(() => {
    setDataPoints([]);
    lastFrequencyRef.current = undefined;
  }, []);

  const downloadCsv = useCallback(() => {
    if (dataPoints.length === 0) return;

    // Sort by frequency
    const sortedData = [...dataPoints].sort((a, b) => a.frequency - b.frequency);

    // Create CSV content
    const headers = [
      "Frequency (Hz)",
      "Closed Loop Magnitude",
      "Closed Loop Phase (deg)",
      "Open Loop Magnitude",
      "Open Loop Phase (deg)",
    ];
    const csvRows = [headers.join(",")];

    sortedData.forEach((point) => {
      csvRows.push([
        point.frequency.toFixed(4),
        point.closedLoopMagnitude.toFixed(6),
        point.closedLoopPhase.toFixed(4),
        point.openLoopMagnitude.toFixed(6),
        point.openLoopPhase.toFixed(4),
      ].join(","));
    });

    const csvContent = csvRows.join("\n");
    const filename = filenameWithTimestamp("sysid_bode_data") + ".csv";
    downloadText(filename, csvContent);
  }, [dataPoints]);

  // Start SysID frequency sweep on firmware
  const startSysId = useCallback(() => {
    if (!deviceHandle) {
      return;
    }

    // Clear previous data points when starting a new sweep
    setDataPoints([]);
    lastFrequencyRef.current = undefined;
    setSysIdRequested(true);

    // Send START_SYSID command to firmware
    hidSend(deviceHandle, serializeStartSysidRequest({}));
  }, [deviceHandle]);

  // Stop SysID frequency sweep on firmware
  const stopSysId = useCallback(() => {
    if (!deviceHandle) {
      return;
    }

    setSysIdRequested(false);

    // Send STOP_SYSID command to firmware
    hidSend(deviceHandle, serializeStopSysidRequest({}));
  }, [deviceHandle]);

  // Detect when SysID completes (frequency goes to 0 or undefined while we requested it)
  useEffect(() => {
    if (sysIdRequested) {
      const freq = sysIdTelemetry.excitationFrequency;
      // If frequency is 0 or undefined and we have data, sweep completed
      if ((freq === undefined || freq === 0) && dataPoints.length > 0) {
        setSysIdRequested(false);
      }
    }
  }, [sysIdTelemetry.excitationFrequency, sysIdRequested, dataPoints.length]);

  const formatValue = (value: number | undefined, decimals: number = 4): string => {
    if (value === undefined) return "---";
    return value.toFixed(decimals);
  };

  // SysID is running if user requested it OR firmware reports active frequency
  const isRunning = sysIdRequested || (
    sysIdTelemetry.excitationFrequency !== undefined &&
    sysIdTelemetry.excitationFrequency > 0
  );

  // Show unavailable message for non-position control modes
  if (!isPositionMode && isConnected) {
    return (
      <div style={configContainerStyle}>
        <div style={screenTitleStyle}>System Identification</div>
        <div
          style={{
            backgroundColor: "#4a3c1c",
            padding: 16,
            borderRadius: 8,
            marginBottom: 16,
            textAlign: "center",
          }}
        >
          <div style={{ color: "#ffcc00", fontSize: 16, fontWeight: "bold", marginBottom: 8 }}>
            Unavailable
          </div>
          <div style={{ color: Colors.WHITE, fontSize: 14 }}>
            System Identification is only available in Position control mode.
            <br />
            Please change the Control Mode to Position in the Throttle Settings screen and flash the configuration to the device.
          </div>
        </div>
        {/* Navigation buttons */}
        <div style={{ display: "flex", width: FORM_WIDTH }}>
          <div style={{ flex: 1 }}>
            <Button
              backgroundColor={Colors.WHITE}
              textColor="#000000"
              onClick={prev}
              disabled={prev === undefined}
            >
              Back
            </Button>
          </div>
          <div style={{ width: 8 }} />
          <div style={{ flex: 1 }}>
            <Button
              backgroundColor={Colors.BUTTON_GREEN}
              textColor={Colors.WHITE}
              onClick={next}
              disabled={next === undefined}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={configContainerStyle}>
      <div style={screenTitleStyle}>System Identification</div>

      <div
        style={{
          color: Colors.WHITE,
          marginBottom: 12,
          paddingLeft: 8,
          paddingRight: 8,
          maxWidth: PLOT_WIDTH,
          textAlign: "center",
          fontSize: 13,
        }}
      >
        Frequency sweep (1-100 Hz) to characterize closed-loop position control response.
      </div>

      {/* Status indicator */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: 12,
          padding: "6px 12px",
          backgroundColor: isRunning
            ? Colors.BUTTON_GREEN
            : isConnected
            ? Colors.DARK_GREEN
            : "#4a1c1c",
          borderRadius: 4,
        }}
      >
        <div
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: isRunning ? "#00ff00" : isConnected ? "#ffcc00" : "#ff4444",
            marginRight: 8,
          }}
        />
        <span style={{ color: Colors.WHITE, fontWeight: "bold", fontSize: 13 }}>
          {isRunning
            ? `Running - ${formatValue(sysIdTelemetry.excitationFrequency, 1)} Hz`
            : isConnected
            ? "Ready"
            : "No Device Connected"}
        </span>
      </div>

      {/* Main content grid */}
      <div
        style={{
          display: "flex",
          gap: 16,
          marginBottom: 12,
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        {/* Left column: Throttle Control (Dashboard-style) */}
        <div
          style={{
            backgroundColor: Colors.DARK_GREEN,
            padding: 12,
            borderRadius: 8,
            minWidth: 280,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div
            style={{
              color: Colors.WHITE,
              fontWeight: "bold",
              marginBottom: 10,
              fontSize: 14,
              borderBottom: "1px solid white",
              paddingBottom: 6,
              width: "100%",
              textAlign: "center",
            }}
          >
            Position Command
          </div>

          {!isUsbMode && (
            <div style={{ color: "#ffcc00", fontSize: 11, marginBottom: 8 }}>
              Set Input Mode to USB to enable position control
            </div>
          )}

          {/* Start/Stop Button */}
          <BPButton
            large
            intent={isEnabled ? "danger" : "success"}
            onClick={() => {
              const newValue = !isEnabled;
              isEnabledRef.current = newValue;
              setIsEnabled(newValue);
            }}
            disabled={!isConnected || !isUsbMode}
            style={{ minWidth: 120, marginBottom: 12 }}
          >
            {isEnabled ? "Stop" : "Start"}
          </BPButton>

          {/* Throttle with vertical slider and spinner (Dashboard-style) */}
          <div style={{ display: "flex", alignItems: "center", height: 200, gap: 16 }}>
            {/* Vertical Slider */}
            <div style={{ height: "100%" }}>
              <Slider
                className="throttle-slider"
                min={inputLower}
                max={inputUpper}
                value={throttleValue}
                onChange={onThrottleChange}
                labelValues={[inputLower, inputCenter, inputUpper]}
                vertical={true}
                showTrackFill={false}
                stepSize={stepSize}
              />
            </div>

            {/* Spinner and Input */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
              <div style={{ position: "relative", width: 100, height: 100 }}>
                <Spinner
                  value={(throttleValue - inputLower) / Math.max(inputUpper - inputLower, 1)}
                  intent={throttleValue < inputCenter ? Intent.DANGER : Intent.SUCCESS}
                  size={100}
                  style={{
                    transform: "rotate(180deg)",
                    scale: throttleValue < inputCenter ? "-1 1" : undefined,
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    color: Colors.WHITE,
                    fontFamily: "monospace",
                    fontSize: 14,
                    fontWeight: "bold",
                  }}
                >
                  {throttleValue.toFixed(0)}
                </div>
              </div>

              <NumericInput
                placeholder={throttleValue.toString()}
                min={inputLower}
                max={inputUpper}
                style={{
                  width: 80,
                  borderRadius: 8,
                  backgroundColor: Colors.LIGHT_GREEN,
                  color: Colors.WHITE,
                }}
                clampValueOnBlur={true}
                onValueChange={(_val, valStr) => setManualValueStr(valStr)}
                value={manualValueStr}
                rightElement={
                  <BPButton icon="arrow-right" minimal={true} intent="success" onClick={updateWithManualValue} />
                }
                buttonPosition="none"
                onKeyDown={(e) => (e.key === "Enter" ? updateWithManualValue() : undefined)}
              />

              <BPButton
                intent="primary"
                onClick={() => setThrottleValue(inputCenter)}
                small
              >
                Center
              </BPButton>
            </div>
          </div>

          <div
            style={{
              marginTop: 10,
              paddingTop: 8,
              borderTop: "1px solid #444",
              display: "flex",
              justifyContent: "space-between",
              fontSize: 12,
              width: "100%",
            }}
          >
            <span style={{ color: Colors.WHITE, opacity: 0.8 }}>Current Position:</span>
            <span style={{ color: "#00ff00", fontFamily: "monospace" }}>
              {formatValue(sysIdTelemetry.motorPosition, 2)}
            </span>
          </div>
        </div>

        {/* Right column: Telemetry */}
        <div
          style={{
            backgroundColor: Colors.DARK_GREEN,
            padding: 12,
            borderRadius: 8,
            minWidth: 240,
          }}
        >
          <div
            style={{
              color: Colors.WHITE,
              fontWeight: "bold",
              marginBottom: 10,
              fontSize: 14,
              borderBottom: "1px solid white",
              paddingBottom: 6,
            }}
          >
            SysID Telemetry
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: Colors.WHITE, opacity: 0.8 }}>Frequency:</span>
              <span style={{ color: "#00ff00", fontFamily: "monospace" }}>
                {formatValue(sysIdTelemetry.excitationFrequency, 2)} Hz
              </span>
            </div>

            <div style={{ marginTop: 4, paddingTop: 4, borderTop: "1px solid #333", fontWeight: "bold", color: "#00ff00" }}>
              Closed Loop
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: Colors.WHITE, opacity: 0.8 }}>Magnitude:</span>
              <span style={{ color: "#00ff00", fontFamily: "monospace" }}>
                {formatValue(sysIdTelemetry.closedLoopMagnitude, 4)}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: Colors.WHITE, opacity: 0.8 }}>Phase:</span>
              <span style={{ color: "#ffff00", fontFamily: "monospace" }}>
                {formatValue(sysIdTelemetry.closedLoopPhase, 2)} deg
              </span>
            </div>

            <div style={{ marginTop: 4, paddingTop: 4, borderTop: "1px solid #333", fontWeight: "bold", color: "#ff8800" }}>
              Open Loop
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: Colors.WHITE, opacity: 0.8 }}>Magnitude:</span>
              <span style={{ color: "#ff8800", fontFamily: "monospace" }}>
                {formatValue(sysIdTelemetry.openLoopMagnitude, 4)}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: Colors.WHITE, opacity: 0.8 }}>Phase:</span>
              <span style={{ color: "#ff00ff", fontFamily: "monospace" }}>
                {formatValue(sysIdTelemetry.openLoopPhase, 2)} deg
              </span>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 4,
                paddingTop: 4,
                borderTop: "1px solid #444",
              }}
            >
              <span style={{ color: Colors.WHITE, opacity: 0.8 }}>Data Points:</span>
              <span style={{ color: Colors.WHITE, fontFamily: "monospace" }}>
                {dataPoints.length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Frequency Response Plots - 2x2 Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
          marginBottom: 12,
        }}
      >
        {/* Closed Loop Plots */}
        <FrequencyResponsePlot
          dataPoints={dataPoints}
          width={PLOT_WIDTH / 2 + 50}
          height={PLOT_HEIGHT}
          title="Closed Loop Magnitude"
          yLabel="Magnitude"
          yKey="closedLoopMagnitude"
          color="#00ff00"
        />
        <FrequencyResponsePlot
          dataPoints={dataPoints}
          width={PLOT_WIDTH / 2 + 50}
          height={PLOT_HEIGHT}
          title="Closed Loop Phase"
          yLabel="Phase (deg)"
          yKey="closedLoopPhase"
          color="#ffff00"
        />

        {/* Open Loop Plots */}
        <FrequencyResponsePlot
          dataPoints={dataPoints}
          width={PLOT_WIDTH / 2 + 50}
          height={PLOT_HEIGHT}
          title="Open Loop Magnitude"
          yLabel="Magnitude"
          yKey="openLoopMagnitude"
          color="#ff8800"
        />
        <FrequencyResponsePlot
          dataPoints={dataPoints}
          width={PLOT_WIDTH / 2 + 50}
          height={PLOT_HEIGHT}
          title="Open Loop Phase"
          yLabel="Phase (deg)"
          yKey="openLoopPhase"
          color="#ff00ff"
        />
      </div>

      {/* Control buttons */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <Button
          backgroundColor={isRunning ? "#cc3333" : "#0066cc"}
          textColor={Colors.WHITE}
          onClick={isRunning ? stopSysId : startSysId}
          disabled={!isConnected}
        >
          {isRunning ? "Stop SysID" : "Start SysID"}
        </Button>
        <Button backgroundColor="#aa3333" textColor={Colors.WHITE} onClick={clearData}>
          Clear Data
        </Button>
        <Button
          backgroundColor={Colors.BUTTON_GREEN}
          textColor={Colors.WHITE}
          onClick={downloadCsv}
          disabled={dataPoints.length === 0}
        >
          Download CSV
        </Button>
      </div>

      {/* Navigation buttons */}
      <div style={{ display: "flex", width: FORM_WIDTH }}>
        <div style={{ flex: 1 }}>
          <Button
            backgroundColor={Colors.WHITE}
            textColor="#000000"
            onClick={prev}
            disabled={prev === undefined}
          >
            Back
          </Button>
        </div>
        <div style={{ width: 8 }} />
        <div style={{ flex: 1 }}>
          <Button
            backgroundColor={Colors.BUTTON_GREEN}
            textColor={Colors.WHITE}
            onClick={next}
            disabled={next === undefined}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};
